// Mantenha em sincronia MANUAL com o catálogo do backend (apps/api/src/core/auth/permissions.ts).

export const PERMISSIONS = [
  // Dashboard
  'view:dashboard',

  // Solicitações
  'view:solicitacoes',
  'manage:solicitacoes',

  // Tipos de vagas
  'view:tipos-vagas',
  'manage:tipos-vagas',

  // Vagas
  'view:vagas',
  'manage:vagas',

  // Candidaturas
  'view:candidaturas',
  'manage:candidaturas',

  // Triagem
  'view:triagem',
  'manage:triagem',

  // Kanban
  'view:kanban',
  'manage:kanban',

  // Entrevistas
  'view:entrevistas',
  'manage:entrevistas',

  // Avaliações
  'view:avaliacoes',
  'manage:avaliacoes',

  // Propostas
  'view:propostas',
  'manage:propostas',

  // Colaboradores
  'view:funcionarios',
  'manage:funcionarios',

  // Banco de talentos
  'view:banco-talentos',
  'manage:banco-talentos',

  // Biblioteca
  'view:biblioteca',
  'manage:biblioteca',

  // Perfis
  'view:perfis',
  'manage:perfis',

  // Competências
  'view:competencias',
  'manage:competencias',

  // Mantras
  'manage:mantras',

  // Ciclos de Avaliação (180°/360°)
  'manage:ciclos-avaliacao',
  'avaliar:desempenho',
  'calibrar:avaliacoes',

  // Dashboard Executivo
  'view:dashboard-executivo',
  // Nine Box (Matriz de Talentos)
  'view:nine-box',
  'manage:nine-box',

  // Analytics
  'view:analytics',

  // Métricas
  'view:metricas',

  // Usuários
  'view:usuarios',
  'manage:usuarios',
  'admin:usuarios',

  // Cargos
  'view:cargos',
  'manage:cargos',
  'admin:cargos',

  // Integrações
  'view:integracoes',
  'manage:integracoes',

  // Quadro de Carreira
  'view:quadro-carreira',
  'manage:quadro-carreira',

  // Feedbacks (Gestão de Pessoas)
  'view:feedbacks',
  'view:feedback-individual',
  'view:feedbacks-privados',
  'view:all-feedbacks',
  'manage:feedbacks',
  'export:feedbacks',

  // PDIs (Planos de Desenvolvimento Individual)
  'view:pdis',
  'view:all-pdis',
  'manage:pdis',

  // Avaliação de Desempenho (Gestão de Pessoas)
  'view:avaliacao-desempenho',
  'create:avaliacao-desempenho',

  // Relatórios Gerenciais
  'view:relatorios-gerenciais',
  'export:relatorios-gerenciais',
  // Pesquisa de Clima (GPTW)
  'manage:pesquisa-clima',
  'responder:pesquisa-clima',
] as const

export type Permission = (typeof PERMISSIONS)[number]

export const PERMISSION_CATEGORIES = [
  'Recrutamento',
  'Avaliação & Desenvolvimento',
  'Pessoas',
  'Administração',
  'Analytics',
] as const

export type PermissionCategory = (typeof PERMISSION_CATEGORIES)[number]

export const PERMISSION_METADATA: Record<Permission, { label: string; description: string }> = {
  'view:dashboard': {
    label: 'Visualizar dashboard',
    description: 'Permite acessar o painel principal com resumo de indicadores e atividades recentes.',
  },
  'view:solicitacoes': {
    label: 'Visualizar solicitações',
    description: 'Permite consultar a lista e os detalhes das solicitações de abertura de vagas.',
  },
  'manage:solicitacoes': {
    label: 'Gerenciar solicitações',
    description: 'Permite criar, editar, aprovar e reprovar solicitações de abertura de vagas.',
  },
  'view:tipos-vagas': {
    label: 'Visualizar tipos de vagas',
    description: 'Permite consultar os tipos de vagas cadastrados no sistema.',
  },
  'manage:tipos-vagas': {
    label: 'Gerenciar tipos de vagas',
    description: 'Permite criar, editar e excluir tipos de vagas.',
  },
  'view:vagas': {
    label: 'Visualizar vagas',
    description: 'Permite consultar a lista e os detalhes das vagas abertas e fechadas.',
  },
  'manage:vagas': {
    label: 'Gerenciar vagas',
    description: 'Permite criar, editar, publicar, pausar e excluir vagas.',
  },
  'view:candidaturas': {
    label: 'Visualizar candidaturas',
    description: 'Permite consultar candidaturas recebidas e seus dados detalhados.',
  },
  'manage:candidaturas': {
    label: 'Gerenciar candidaturas',
    description: 'Permite mover, atualizar status e interagir com candidaturas no processo seletivo.',
  },
  'view:triagem': {
    label: 'Visualizar triagem',
    description: 'Permite acessar a etapa de triagem de candidatos e ver as avaliações iniciais.',
  },
  'manage:triagem': {
    label: 'Gerenciar triagem',
    description: 'Permite realizar e editar triagens, classificar e reprovar candidatos nesta etapa.',
  },
  'view:kanban': {
    label: 'Visualizar kanban',
    description: 'Permite visualizar o quadro kanban do processo seletivo com todos os candidatos.',
  },
  'manage:kanban': {
    label: 'Gerenciar kanban',
    description: 'Permite mover candidatos entre colunas e gerenciar o fluxo do kanban.',
  },
  'view:entrevistas': {
    label: 'Visualizar entrevistas',
    description: 'Permite consultar entrevistas agendadas e seus registros de feedback.',
  },
  'manage:entrevistas': {
    label: 'Gerenciar entrevistas',
    description: 'Permite agendar, editar, cancelar entrevistas e registrar resultados.',
  },
  'view:avaliacoes': {
    label: 'Visualizar avaliações',
    description: 'Permite consultar avaliações de candidatos realizadas durante o processo seletivo.',
  },
  'manage:avaliacoes': {
    label: 'Gerenciar avaliações',
    description: 'Permite criar e editar avaliações de candidatos no processo seletivo.',
  },
  'view:propostas': {
    label: 'Visualizar propostas',
    description: 'Permite consultar propostas enviadas a candidatos aprovados.',
  },
  'manage:propostas': {
    label: 'Gerenciar propostas',
    description: 'Permite criar, editar, enviar e revogar propostas a candidatos.',
  },
  'view:funcionarios': {
    label: 'Visualizar colaboradores',
    description: 'Permite consultar a lista e o perfil detalhado dos colaboradores cadastrados.',
  },
  'manage:funcionarios': {
    label: 'Gerenciar colaboradores',
    description: 'Permite cadastrar, editar e desativar colaboradores.',
  },
  'view:banco-talentos': {
    label: 'Visualizar banco de talentos',
    description: 'Permite consultar candidatos e profissionais no banco de talentos.',
  },
  'manage:banco-talentos': {
    label: 'Gerenciar banco de talentos',
    description: 'Permite adicionar, editar e remover profissionais do banco de talentos.',
  },
  'view:biblioteca': {
    label: 'Visualizar biblioteca',
    description: 'Permite acessar e visualizar os materiais e documentos da biblioteca.',
  },
  'manage:biblioteca': {
    label: 'Gerenciar biblioteca',
    description: 'Permite adicionar, editar e excluir materiais e documentos da biblioteca.',
  },
  'view:perfis': {
    label: 'Visualizar perfis',
    description: 'Permite consultar os perfis comportamentais e profissionais cadastrados.',
  },
  'manage:perfis': {
    label: 'Gerenciar perfis',
    description: 'Permite criar, editar e excluir perfis comportamentais e profissionais.',
  },
  'view:competencias': {
    label: 'Visualizar competências',
    description: 'Permite consultar as competências definidas no sistema.',
  },
  'manage:competencias': {
    label: 'Gerenciar competências',
    description: 'Permite criar, editar e excluir competências e seus critérios de avaliação.',
  },
  'manage:mantras': {
    label: 'Gerenciar mantras',
    description: 'Permite criar, editar e excluir mantras do glossário de fit cultural.',
  },
  'manage:ciclos-avaliacao': {
    label: 'Gerenciar ciclos de avaliação',
    description:
      'Permite criar, configurar (pesos e KPIs), editar e abrir/fechar ciclos de avaliação 180° e 360°.',
  },
  'view:dashboard-executivo': {
    label: 'Visualizar Dashboard Executivo',
    description:
      'Permite acessar a visão gerencial agregada de performance e desenvolvimento (RH vê tudo, gestor vê só a própria equipe).',
  },
  'avaliar:desempenho': {
    label: 'Preencher avaliações de ciclo',
    description:
      'Permite visualizar e preencher os formulários de avaliação (180°/360°) em que o usuário foi designado como avaliador.',
  },
  'calibrar:avaliacoes': {
    label: 'Calibrar avaliações',
    description:
      'Permite conduzir a calibragem de um ciclo: comparar notas por avaliador, identificar divergências e registrar ajustes com justificativa.',
  },
  'view:nine-box': {
    label: 'Visualizar Nine Box',
    description:
      'Permite consultar a matriz de talentos (Performance × Potencial) do ciclo, restrita à própria equipe quando gestor.',
  },
  'manage:nine-box': {
    label: 'Gerenciar Nine Box',
    description:
      'Permite registrar e atualizar a nota de potencial dos colaboradores na matriz de talentos.',
  },
  'view:analytics': {
    label: 'Visualizar analytics',
    description: 'Permite acessar relatórios analíticos e gráficos sobre processos seletivos e pessoas.',
  },
  'view:metricas': {
    label: 'Visualizar métricas',
    description: 'Permite acessar indicadores de desempenho e métricas operacionais do RH.',
  },
  'view:usuarios': {
    label: 'Visualizar usuários',
    description: 'Permite consultar a lista de usuários do sistema e seus dados básicos.',
  },
  'manage:usuarios': {
    label: 'Gerenciar usuários',
    description: 'Permite criar, editar, ativar e desativar usuários do sistema.',
  },
  'admin:usuarios': {
    label: 'Administrar usuários',
    description: 'Permite gerenciar permissões, cargos e configurações sensíveis de usuários.',
  },
  'view:cargos': {
    label: 'Visualizar cargos',
    description: 'Permite consultar os cargos cadastrados e suas permissões associadas.',
  },
  'manage:cargos': {
    label: 'Gerenciar cargos',
    description: 'Permite criar, editar e excluir cargos no sistema.',
  },
  'admin:cargos': {
    label: 'Administrar cargos',
    description: 'Permite gerenciar permissões e configurações sensíveis de cargos.',
  },
  'view:integracoes': {
    label: 'Visualizar integrações',
    description: 'Permite consultar as integrações configuradas com sistemas externos.',
  },
  'manage:integracoes': {
    label: 'Gerenciar integrações',
    description: 'Permite configurar, editar e desativar integrações com sistemas externos.',
  },
  'view:quadro-carreira': {
    label: 'Visualizar quadro de carreira',
    description: 'Permite consultar o plano de carreira e trilhas de crescimento dos colaboradores.',
  },
  'manage:quadro-carreira': {
    label: 'Gerenciar quadro de carreira',
    description: 'Permite criar e editar planos de carreira e trilhas de crescimento.',
  },
  'view:feedbacks': {
    label: 'Visualizar feedbacks',
    description: 'Permite consultar feedbacks registrados entre gestores e colaboradores.',
  },
  'view:feedback-individual': {
    label: 'Visualizar feedback individual',
    description: 'Permite abrir e ver o conteúdo completo de um feedback específico (separado da listagem).',
  },
  'view:feedbacks-privados': {
    label: 'Visualizar feedbacks privados',
    description:
      'Permite ver feedbacks marcados como FECHADOS (privados), que o colaborador avaliado não visualiza.',
  },
  'view:all-feedbacks': {
    label: 'Visualizar todos os feedbacks',
    description:
      'Permite acessar a listagem administrativa com todos os feedbacks da plataforma, com filtros e busca.',
  },
  'view:all-pdis': {
    label: 'Visualizar todos os PDIs',
    description:
      'Permite acessar a listagem administrativa com todos os PDIs da plataforma, de todos os colaboradores.',
  },
  'manage:feedbacks': {
    label: 'Gerenciar feedbacks',
    description: 'Permite criar, editar e excluir feedbacks de desempenho.',
  },
  'export:feedbacks': {
    label: 'Exportar feedbacks',
    description: 'Permite exportar o relatório detalhado de feedbacks em CSV.',
  },
  'view:pdis': {
    label: 'Visualizar PDIs',
    description: 'Permite consultar os Planos de Desenvolvimento Individual dos colaboradores.',
  },
  'manage:pdis': {
    label: 'Gerenciar PDIs',
    description: 'Permite criar, editar e acompanhar Planos de Desenvolvimento Individual.',
  },
  'view:avaliacao-desempenho': {
    label: 'Visualizar avaliações de desempenho',
    description: 'Permite consultar as avaliações de desempenho dos colaboradores e o histórico.',
  },
  'create:avaliacao-desempenho': {
    label: 'Registrar avaliação de desempenho',
    description: 'Permite avaliar o desempenho de um colaborador nas competências do cargo.',
  },
  'view:relatorios-gerenciais': {
    label: 'Visualizar relatórios gerenciais',
    description: 'Permite consultar os relatórios gerenciais de feedbacks, turnover e desempenho.',
  },
  'export:relatorios-gerenciais': {
    label: 'Exportar relatórios gerenciais',
    description: 'Permite exportar o ranking de líderes em CSV.',
  },
  'manage:pesquisa-clima': {
    label: 'Gerenciar pesquisa de clima',
    description:
      'Permite criar, abrir e fechar pesquisas de clima (GPTW) e visualizar os resultados agregados.',
  },
  'responder:pesquisa-clima': {
    label: 'Responder pesquisa de clima',
    description: 'Permite responder pesquisas de clima abertas (GPTW), de forma anônima.',
  },
}

/** Agrupa permissões por domínio pra exibir a matriz. */
export const PERMISSION_GROUPS: {
  label: string
  permissions: Permission[]
  category: PermissionCategory
}[] = [
  { label: 'Dashboard', permissions: ['view:dashboard'], category: 'Recrutamento' },
  {
    label: 'Solicitações',
    permissions: ['view:solicitacoes', 'manage:solicitacoes'],
    category: 'Recrutamento',
  },
  {
    label: 'Tipos de Vagas',
    permissions: ['view:tipos-vagas', 'manage:tipos-vagas'],
    category: 'Recrutamento',
  },
  { label: 'Vagas', permissions: ['view:vagas', 'manage:vagas'], category: 'Recrutamento' },
  {
    label: 'Candidaturas',
    permissions: ['view:candidaturas', 'manage:candidaturas'],
    category: 'Recrutamento',
  },
  {
    label: 'Triagem',
    permissions: ['view:triagem', 'manage:triagem'],
    category: 'Recrutamento',
  },
  {
    label: 'Kanban',
    permissions: ['view:kanban', 'manage:kanban'],
    category: 'Recrutamento',
  },
  {
    label: 'Entrevistas',
    permissions: ['view:entrevistas', 'manage:entrevistas'],
    category: 'Recrutamento',
  },
  {
    label: 'Avaliações',
    permissions: ['view:avaliacoes', 'manage:avaliacoes'],
    category: 'Recrutamento',
  },
  {
    label: 'Propostas',
    permissions: ['view:propostas', 'manage:propostas'],
    category: 'Recrutamento',
  },
  {
    label: 'Feedbacks',
    permissions: [
      'view:feedbacks',
      'view:feedback-individual',
      'view:feedbacks-privados',
      'view:all-feedbacks',
      'manage:feedbacks',
      'export:feedbacks',
    ],
    category: 'Avaliação & Desenvolvimento',
  },
  {
    label: 'PDIs',
    permissions: ['view:pdis', 'view:all-pdis', 'manage:pdis'],
    category: 'Avaliação & Desenvolvimento',
  },
  {
    label: 'Avaliação de Desempenho',
    permissions: ['view:avaliacao-desempenho', 'create:avaliacao-desempenho'],
    category: 'Avaliação & Desenvolvimento',
  },
  {
    label: 'Quadro de Carreira',
    permissions: ['view:quadro-carreira', 'manage:quadro-carreira'],
    category: 'Avaliação & Desenvolvimento',
  },
  {
    label: 'Competências',
    permissions: ['view:competencias', 'manage:competencias'],
    category: 'Avaliação & Desenvolvimento',
  },
  {
    label: 'Mantras',
    permissions: ['manage:mantras'],
    category: 'Avaliação & Desenvolvimento',
  },
  {
    label: 'Ciclos de Avaliação',
    permissions: ['manage:ciclos-avaliacao', 'avaliar:desempenho', 'calibrar:avaliacoes'],
    category: 'Avaliação & Desenvolvimento',
  },
  {
    label: 'Pesquisa de Clima',
    permissions: ['manage:pesquisa-clima', 'responder:pesquisa-clima'],
    category: 'Avaliação & Desenvolvimento',
  },
  {
    label: 'Dashboard Executivo',
    permissions: ['view:dashboard-executivo'],
    category: 'Avaliação & Desenvolvimento',
  },
  {
    label: 'Nine Box',
    permissions: ['view:nine-box', 'manage:nine-box'],
    category: 'Avaliação & Desenvolvimento',
  },
  {
    label: 'Colaboradores',
    permissions: ['view:funcionarios', 'manage:funcionarios'],
    category: 'Pessoas',
  },
  {
    label: 'Banco de Talentos',
    permissions: ['view:banco-talentos', 'manage:banco-talentos'],
    category: 'Pessoas',
  },
  { label: 'Biblioteca', permissions: ['view:biblioteca', 'manage:biblioteca'], category: 'Pessoas' },
  { label: 'Perfis', permissions: ['view:perfis', 'manage:perfis'], category: 'Pessoas' },
  {
    label: 'Usuários',
    permissions: ['view:usuarios', 'manage:usuarios', 'admin:usuarios'],
    category: 'Administração',
  },
  {
    label: 'Cargos',
    permissions: ['view:cargos', 'manage:cargos', 'admin:cargos'],
    category: 'Administração',
  },
  {
    label: 'Integrações',
    permissions: ['view:integracoes', 'manage:integracoes'],
    category: 'Administração',
  },
  { label: 'Analytics', permissions: ['view:analytics'], category: 'Analytics' },
  { label: 'Métricas', permissions: ['view:metricas'], category: 'Analytics' },
  {
    label: 'Relatórios Gerenciais',
    permissions: ['view:relatorios-gerenciais', 'export:relatorios-gerenciais'],
    category: 'Analytics',
  },
]

export function hasAnyPermission(
  userPerms: readonly string[] | undefined,
  required: readonly string[],
): boolean {
  if (required.length === 0) return true
  if (!userPerms || userPerms.length === 0) return false
  return required.some((p) => userPerms.includes(p))
}

export function hasAllPermissions(
  userPerms: readonly string[] | undefined,
  required: readonly string[],
): boolean {
  if (required.length === 0) return true
  if (!userPerms || userPerms.length === 0) return false
  return required.every((p) => userPerms.includes(p))
}
